/*
  Introduce tiptap extension for text alignment.

  As tiptap extensions do not include text alignment formatting, we will need to create our own implementation.
  This class implements the text alignment extension which supports `left`, `right`, `center`, and `justify`.

  An implementation example,

  ```js
  <el-button
    type="text"
    :class="{ 'is-active': isActive.align_text({ align: 'left' }) }"
    @click="commands.align_text({ align: 'left' })"
  >Left</el-button>
  <el-button
    type="text"
    :class="{ 'is-active': isActive.align_text({ align: 'right' }) }"
    @click="commands.align_text({ align: 'right' })"
  >Right</el-button>
  ```

  The plugin is inspired by https://gist.github.com/waaronking/0e1712e1c1aea1d73b0cc2cb4129edde?ts=2 via
  https://github.com/ueberdosis/tiptap/pull/544 and tiptap-extensions/src/nodes/Heading.js.
 */
import {Node} from 'tiptap'
import {toggleBlockType} from 'tiptap-commands'

export default class AlignText extends Node {
  get name () {
    return 'align_text'
  }

  get defaultOptions () {
    return {
      alignments: ['center', 'justify', 'left', 'right']
    }
  }

  get schema () {
    return {
      attrs: {
        align: {
          default: 'left'
        }
      },
      content: 'inline*',
      group: 'block',
      defining: true,
      draggable: false,
      parseDOM: this.options.alignments.map(align => ({
        // Set higher priority than the paragraph node, with default priority value of 50.
        priority: 51,
        tag: 'p',
        // From ProseMirror Doc: getAttrs: ? fn(dom.Node | string) -> ? Object | false
        // the return of object allows toDOM to set the correct text-align value.
        getAttrs: node => node.style.textAlign === align ? {align} : false
      })),
      // The decision to use p instead of span is to honor the line spacing and margin set
      // for the overall p tags in the note editor.
      toDOM: node => ['p', {style: `text-align: ${node.attrs.align}`}, 0]
    }
  }

  commands ({ type, schema }) {
    return attrs => toggleBlockType(type, schema.nodes.align_text, attrs)
  }
}
